'use client';
import { resetAdditionalData } from '@/redux/additionalDataSlice';
import { errorReason } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, FormHelperText, Stack, Typography } from '@mui/material';
import { submitStep } from '@repo/onb-api';
import { Doodles, SafeSpacePadder } from '@repo/ui';
import { deleteClientCookie, getClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export default function OnbError({ message, hideButtons }: { message?: string; hideButtons?: boolean }) {
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);
  const { errorReason } = useSelector((state: RootState) => state.navigationdata);
  const isInternal = useSelector((state: RootState) => state.additionalData.internal);
  const instanceId = getClientCookie('instanceId');
  const instanceIdFromRedux = useSelector((state: RootState) => state.flowdata.id);
  const isCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';
  const smsInterval = useRef(0);
  const [smsTimeout, setSmsTimeout] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (instanceId && !instanceIdFromRedux) {
      router.push('/');
    } else {
      setIsReady(true);
    }
  }, [instanceId, instanceIdFromRedux, router]);

  useEffect(() => {
    smsInterval.current = window.setTimeout(() => {
      setSmsTimeout((t) => t - 1);
    }, 1000);
    if (smsTimeout <= 0) clearTimeout(smsInterval.current);
  }, [smsTimeout]);

  const returnAction = () => {
    if (isCuotas && !isInternal) {
      router.push('/back_to_store');
    } else {
      router.push('/');
    }
  };

  const reasonMessage: Record<errorReason, string> = {
    slowService: 'El servicio está un poco lento, por favor reintentalo a la brevedad.',
    timeout: 'Tu tiempo de sesión se agotó, por favor volvé a intentarlo.',
    accessKey: 'Por favor continuá desde el link enviado a tu celular.',
  };

  if (!isReady) return null;

  return (
    <SafeSpacePadder
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
        gap: 4,
        height: '100%',
        width: '100%',
        paddingBottom: { xs: 4, md: 8 },
      }}
    >
      <Stack alignItems="center" gap={4} height={'100%'} justifyContent={'center'}>
        <Doodles type={errorReason === 'accessKey' ? 'waiting' : 'end'} />
        <Stack gap={2}>
          <Typography variant="h1">
            {errorReason ? reasonMessage[errorReason] : message || 'Ups! Ha habido un error'}
          </Typography>
        </Stack>
      </Stack>
      {!hideButtons && (
        <Stack gap={2} width={'100%'}>
          {errorReason === 'accessKey' ? (
            instanceId && (
              <>
                <Button
                  disabled={smsTimeout > 0}
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setSmsTimeout(45);
                    submitStep({
                      instanceId,
                      step: {
                        name: 'REVIEW_AND_SEND_LINK',
                      },
                    });
                  }}
                >
                  Reenviar mensaje
                </Button>
                <FormHelperText sx={{ alignSelf: 'center', py: 1, display: smsTimeout > 0 ? 'block' : 'none' }}>
                  Código enviado. Para reenviar otro por favor espere 00:{`${smsTimeout}`.padStart(2, '0')}
                </FormHelperText>
              </>
            )
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                router.push('/');
                router.refresh();
              }}
            >
              Reintentar
            </Button>
          )}
          <Button
            sx={{ display: !isCuotas || !isInternal ? 'block' : 'none' }}
            fullWidth
            variant="inverted"
            onClick={() => {
              deleteClientCookie([
                'instanceId',
                'requestedAmount',
                'integrationType',
                'policyProduct',
                'utmData',
                'orderToken',
                'accessKey',
              ]);
              dispatch(resetAdditionalData());
              returnAction();
              router.refresh();
            }}
          >
            {isCuotas && !isInternal ? 'Volver a la tienda' : 'Volver al inicio'}
          </Button>
        </Stack>
      )}
    </SafeSpacePadder>
  );
}
